import React, { useState, useRef, useEffect } from 'react';

interface OptimizedImageProps {
  src: string;
  alt: string;
  width?: number;
  height?: number;
  priority?: boolean;
  loading?: 'lazy' | 'eager';
  className?: string;
  style?: React.CSSProperties;
  onClick?: () => void;
}

const OptimizedImage: React.FC<OptimizedImageProps> = ({
  src,
  alt,
  width,
  height,
  priority = false,
  loading,
  className,
  style,
  onClick,
}) => {
  const [isLoaded, setIsLoaded] = useState(false);
  const [isInView, setIsInView] = useState(false);
  const imgRef = useRef<HTMLImageElement>(null);
  
  // Determine loading strategy
  const loadingStrategy = loading || (priority ? 'eager' : 'lazy');
  
  // Intersection Observer for advanced lazy loading
  useEffect(() => {
    if (loadingStrategy === 'eager' || priority) {
      setIsInView(true);
      return;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsInView(true);
          observer.disconnect();
        }
      },
      {
        rootMargin: '50px', // Start loading 50px before entering viewport
        threshold: 0.1,
      }
    );

    if (imgRef.current) {
      observer.observe(imgRef.current);
    }

    return () => observer.disconnect();
  }, [loadingStrategy, priority]);
  
  // Extract file extension and create WebP version
  const getWebPSrc = (originalSrc: string) => {
    // Handle undefined or empty src
    if (!originalSrc || typeof originalSrc !== 'string') {
      return originalSrc;
    }
    
    // Handle external URLs (keep as-is)
    if (originalSrc.startsWith('http')) {
      return originalSrc;
    }
    
    // Convert local images to WebP
    const lastDotIndex = originalSrc.lastIndexOf('.');
    if (lastDotIndex === -1) return originalSrc;
    
    return originalSrc.substring(0, lastDotIndex) + '.webp';
  };

  const webpSrc = getWebPSrc(src);
  const isExternal = src && src.startsWith('http');

  // Return null if no src is provided
  if (!src) {
    return null;
  }

  // Placeholder div while waiting for intersection
  if (!isInView && loadingStrategy === 'lazy' && !priority) {
    return (
      <div
        ref={imgRef}
        style={{
          width: width || 'auto',
          height: height || 'auto',
          backgroundColor: '#f5f5f5',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          ...style,
        }}
        className={className}
      >
        {/* Optional: Add a subtle loading indicator */}
      </div>
    );
  }

  // For external images, use simple img tag
  if (isExternal) {
    return (
      <img
        ref={imgRef}
        src={src}
        alt={alt}
        width={width}
        height={height}
        loading={loadingStrategy}
        className={className}
        style={{
          transition: 'opacity 0.3s ease',
          opacity: isLoaded ? 1 : 0,
          ...style,
        }}
        onLoad={() => setIsLoaded(true)}
        onClick={onClick}
      />
    );
  }

  // For local images, use picture element with WebP fallback
  return (
    <picture>
      <source srcSet={webpSrc} type="image/webp" />
      <img
        ref={imgRef}
        src={src}
        alt={alt}
        width={width}
        height={height}
        loading={loadingStrategy}
        className={className}
        style={{
          transition: 'opacity 0.3s ease',
          opacity: isLoaded ? 1 : 0,
          ...style,
        }}
        onLoad={() => setIsLoaded(true)}
        onClick={onClick}
      />
    </picture>
  );
};

export default OptimizedImage;
